import { Badge, Center, Group, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { dateToEndOfDay, dateToStartOfDay, daysAgoISODate, todayISODate } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';
import DateRangePicker from '../DateRangePicker/DateRangePicker';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import { PaginationBar, ScreenHint, formatDate } from './shared';

// Shared with KnowledgeBaseTab so the tab badge's first count covers the same window this
// screen opens on.
export const UNANSWERED_DEFAULT_DAYS = 30;

// Display-only view over live conversation data: which shopper questions the
// assistant had no material for. Answering happens through the Q&A Review screen.
//
// The window is sent as `from`/`to` ISO timestamps in the account's local zone — the
// house convention (SuperAdminOperationalEmails.jsx does the same). The API has to read
// those rather than the older `days=N`: if it ignores them it silently serves its own
// default window, which looks like a correct result. KnowledgeBaseTab's badge count is
// fed from `onTotalChange` below so it can't drift from what this list is showing.
const UnansweredScreen = ({ apiBase, onTotalChange }) => {
  const fetch = useFetch();

  const [startDate, setStartDate] = useState(daysAgoISODate(UNANSWERED_DEFAULT_DAYS - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [apiBase]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({
      page: String(page),
      perPage: String(perPage),
      from: dateToStartOfDay(startDate, 'local'),
      to: dateToEndOfDay(endDate, 'local'),
    });
    fetch(`${apiBase}/unanswered?${params}`)
      .then(({ queries, total: totalCount }) => {
        if (cancelled) return;
        setError(null);
        // The window is live — a page that emptied under us snaps back to the first.
        if (!queries.length && totalCount > 0 && page > 1) {
          setPage(1);
          return;
        }
        setRows(queries);
        setTotal(totalCount);
        // Keeps the tab badge on the window actually being shown.
        if (onTotalChange) onTotalChange(totalCount);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, apiBase, startDate, endDate, page, perPage, onTotalChange]);

  if (error) {
    return <ErrorMessage message={error.message || 'Could not load unanswered questions.'} />;
  }
  if (rows === null) {
    return <Spinner />;
  }

  return (
    <div>
      <Group justify="flex-end" align="center" wrap="wrap" gap={8} mb="sm">
        <ScreenHint text="Questions shoppers actually asked that the assistant had no material for — read live from the conversation history, most-asked first. Cover a gap by adding a document or a Q&A pair for it." />
        <DateRangePicker
          preSetRanges
          numberOfColumns={1}
          startDate={startDate}
          endDate={endDate}
          setStartDate={(value) => {
            setStartDate(value);
            setPage(1);
          }}
          setEndDate={(value) => {
            setEndDate(value);
            setPage(1);
          }}
          setDateRange={(start, end) => {
            setStartDate(start);
            setEndDate(end);
            setPage(1);
          }}
        />
      </Group>

      {rows.length === 0 ? (
        <Center py={60}>
          <Stack align="center" gap="xs">
            <IconSearch size={44} stroke={1} color="var(--mantine-color-gray-5)" />
            <Text fw={500}>No unanswered questions</Text>
            <Text size="sm" c="dimmed">
              Every shopper question in this period found supporting material.
            </Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap={8}>
          {rows.map((row) => (
            <Paper key={row.key} withBorder p="sm" radius="sm">
              <Group gap={10} wrap="wrap" align="center">
                <Text size="sm" fw={600} style={{ flex: 1, minWidth: 260 }}>
                  {row.query}
                </Text>
                <Tooltip label="How many times shoppers asked this in the selected period." withArrow>
                  <Badge variant="light" color="info" size="sm">
                    asked {row.count}×
                  </Badge>
                </Tooltip>
                <Text fz={12} c="dimmed">
                  first {formatDate(row.firstAskedAt)} · last {formatDate(row.lastAskedAt)}
                </Text>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      {total > 0 && (
        <PaginationBar
          shown={rows.length}
          total={total}
          noun="questions"
          page={page}
          onPageChange={setPage}
          perPage={perPage}
          onPerPageChange={(value) => {
            setPerPage(value);
            setPage(1);
          }}
          mt="sm"
        />
      )}
    </div>
  );
};

export default UnansweredScreen;
